import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';

const ReceiptCustomization = ({ apiClient }) => {
  const [receiptConfig, setReceiptConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    business_address: '',
    business_phone: '',
    business_email: '',
    tax_id: '',
    additional_header_text: '',
    footer_text: '',
    show_tax_breakdown: true,
    paper_width: 80
  });

  useEffect(() => {
    fetchReceiptConfig();
  }, []);

  const fetchReceiptConfig = async () => {
    try {
      const response = await apiClient.get('/restaurant/receipt-config');
      setReceiptConfig(response.data);
      setFormData({
        business_name: response.data.business_name || '',
        business_address: response.data.business_address || '',
        business_phone: response.data.business_phone || '',
        business_email: response.data.business_email || '',
        tax_id: response.data.tax_id || '',
        additional_header_text: response.data.additional_header_text || '',
        footer_text: response.data.footer_text || 'Thank you for your business!',
        show_tax_breakdown: response.data.show_tax_breakdown !== false,
        paper_width: response.data.paper_width || 80
      });
    } catch (error) {
      console.error('Error fetching receipt config:', error);
      toast.error('Failed to load receipt configuration');
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await apiClient.put('/restaurant/receipt-config', formData);
      toast.success('Receipt configuration updated successfully!');
      fetchReceiptConfig();
    } catch (error) {
      console.error('Error saving receipt config:', error);
      toast.error('Failed to update receipt configuration');
    } finally {
      setLoading(false);
    }
  };

  const generatePreviewReceipt = () => {
    const now = new Date();
    return `
${'='.repeat(40)}
${(formData.business_name || 'YOUR BUSINESS NAME').toUpperCase().slice(0, 40)}
${formData.additional_header_text ? formData.additional_header_text : ''}
${'='.repeat(40)}
Address: ${formData.business_address || 'Your business address'}
Phone: ${formData.business_phone || 'Your phone number'}
${formData.business_email ? `Email: ${formData.business_email}` : ''}
${formData.tax_id ? `Tax ID: ${formData.tax_id}` : ''}

Order #12345678
Date: ${now.toLocaleDateString()} ${now.toLocaleTimeString()}
Table: Table 1
Customer: John Doe

${'-'.repeat(40)}
ITEMS:
${'-'.repeat(40)}
2x Burger                     $24.00
1x Fries                       $8.00
2x Coke                       $10.00

${'-'.repeat(40)}
Subtotal: $42.00
${formData.show_tax_breakdown ? `Sales Tax (8.5%): $3.57
Service Charge: $2.00
` : ''}Total Tax: $5.57
${'='.repeat(40)}
TOTAL: $47.57
${'='.repeat(40)}

Payment: Stripe
Status: Paid

${formData.footer_text || 'Thank you for your business!'}

${'='.repeat(40)}
    `.trim();
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration Form */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-xl font-semibold text-gray-900">Receipt Customization</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="business_name" className="form-label">
                  Business Name
                </label>
                <input
                  type="text"
                  id="business_name"
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleInputChange}
                  data-testid="business-name-input"
                  className="form-input"
                  placeholder="Enter business name for receipts"
                />
              </div>

              <div>
                <label htmlFor="additional_header_text" className="form-label">
                  Additional Header Text
                </label>
                <textarea
                  id="additional_header_text"
                  name="additional_header_text"
                  value={formData.additional_header_text}
                  onChange={handleInputChange}
                  data-testid="additional-header-text-input"
                  className="form-textarea"
                  rows={2}
                  placeholder="Additional text to appear below business name (e.g., slogan, special message)"
                />
              </div>

              <div>
                <label htmlFor="business_address" className="form-label">
                  Business Address
                </label>
                <textarea
                  id="business_address"
                  name="business_address"
                  value={formData.business_address}
                  onChange={handleInputChange}
                  data-testid="business-address-input"
                  className="form-textarea"
                  rows={3}
                  placeholder="Enter complete business address"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="business_phone" className="form-label">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="business_phone"
                    name="business_phone"
                    value={formData.business_phone}
                    onChange={handleInputChange}
                    data-testid="business-phone-input"
                    className="form-input"
                    placeholder="Phone number"
                  />
                </div>
                <div>
                  <label htmlFor="business_email" className="form-label">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="business_email"
                    name="business_email"
                    value={formData.business_email}
                    onChange={handleInputChange}
                    data-testid="business-email-input"
                    className="form-input"
                    placeholder="business@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="tax_id" className="form-label">
                    Tax ID / License Number
                  </label>
                  <input
                    type="text"
                    id="tax_id"
                    name="tax_id"
                    value={formData.tax_id}
                    onChange={handleInputChange}
                    data-testid="tax-id-input"
                    className="form-input"
                    placeholder="Tax ID or license number"
                  />
                </div>
                <div>
                  <label htmlFor="paper_width" className="form-label">
                    Paper Width (mm)
                  </label>
                  <select
                    id="paper_width"
                    name="paper_width"
                    value={formData.paper_width}
                    onChange={handleInputChange}
                    data-testid="paper-width-select"
                    className="form-input"
                  >
                    <option value={58}>58mm</option>
                    <option value={80}>80mm</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="footer_text" className="form-label">
                  Footer Text
                </label>
                <textarea
                  id="footer_text"
                  name="footer_text"
                  value={formData.footer_text}
                  onChange={handleInputChange}
                  data-testid="footer-text-input"
                  className="form-textarea"
                  rows={2}
                  placeholder="Thank you message or additional information"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="show_tax_breakdown"
                  name="show_tax_breakdown"
                  checked={formData.show_tax_breakdown}
                  onChange={handleInputChange}
                  data-testid="show-tax-breakdown-checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="show_tax_breakdown" className="ml-2 block text-sm text-gray-900">
                  Show detailed tax breakdown on receipts
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="save-receipt-config-button"
              className="w-full btn-primary"
            >
              {loading ? <div className="spinner mx-auto"></div> : 'Save Receipt Configuration'}
            </button>
          </form>
        </div>

        {/* Preview */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-xl font-semibold text-gray-900">Receipt Preview</h2>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <pre 
              className="font-mono whitespace-pre-wrap bg-white p-4 border rounded shadow-sm overflow-x-auto"
              data-testid="receipt-preview"
              style={{ 
                fontSize: formData.paper_width == 58 ? '10px' : '12px',
                lineHeight: formData.paper_width == 58 ? '1.2' : '1.4'
              }}
            >
              {generatePreviewReceipt()}
            </pre>
          </div>

          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Preview Notes</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• This is a sample receipt showing how your customization will appear</li>
              <li>• Actual receipts will include real order data</li>
              <li>• Paper width affects font size for better printing</li>
              <li>• Tax breakdown visibility can be toggled on/off</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptCustomization;